import { queryOptions } from '@tanstack/react-query';

import { fetchBackend } from './apiClient';
import type { CloudResource } from './resource';

export const searchQueries = {
  all: () => ['search'],
  byProject: (projectId: string) => [...searchQueries.all(), projectId],
  searchs: (projectId: string) => [
    ...searchQueries.byProject(projectId),
    'search',
  ],
  search: ({
    projectId,
    searchTerm,
    from,
    to,
    showDeleted,
  }: {
    projectId: string;
    searchTerm: string | undefined;
    from: string | undefined;
    to: string | undefined;
    showDeleted: boolean | undefined;
  }) =>
    queryOptions({
      queryKey: [
        ...searchQueries.searchs(projectId),
        searchTerm,
        from,
        to,
        showDeleted,
      ],
      queryFn: async () => {
        if (searchTerm === undefined) {
          return null;
        }

        const results = await search({
          searchTerm,
          projectId,
          from,
          to,
          showDeleted,
        });

        return results;
      },
    }),
} as const;

export const search = async ({
  searchTerm,
  projectId,
  from,
  to,
  showDeleted,
}: {
  searchTerm: string;
  projectId: string;
  from: string | undefined;
  to: string | undefined;
  showDeleted: boolean | undefined;
}): Promise<SearchData> => {
  const params = new URLSearchParams();
  if (from !== undefined) {
    params.set('from', from);
  }
  if (to !== undefined) {
    params.set('to', to);
  }
  params.set('page', '1');
  if (showDeleted === true) {
    params.set('showDeleted', 'true');
  }

  const response = await fetchBackend(
    `/search/${projectId}/${encodeURIComponent(searchTerm)}?${params.toString()}`,
    {
      method: 'GET',
    },
  );

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const data = (await response.json()) as SearchData;

  return data;
};

export type SearchResource = CloudResource & {
  matchedProperties?: {
    value: string;
    property: string;
    distance: number;
  }[];
};

export type SearchData = {
  results: SearchResource[];
};
